import { apiClient } from "./api";

// ── Sending a candidate a WhatsApp message ──────────────────────────────────
//
// Recruiters were copying the invitation and pasting it into WhatsApp by hand,
// one candidate at a time. This sends it.
//
// ─── WHY NOT /api/auth/sendwpmessage ────────────────────────────────────────
//
// That is the travel dashboard's route and it is welded to a booking: it
// requires `booking_id`, refuses anything whose booking does not exist for the
// store, and on success writes `template_name` into that booking's
// `wp_msg_status`. A candidate has no booking, so it answers
//
//   400 "store_id, booking_id, and template_name are mandatory"
//
// and the only way to make it accept us would be to make those checks optional
// for everybody — including the screen they were written for. The hiring module
// has its own route on the same gateway instead:
//
//   POST /api/hiring/interview/whatsapp/:application_id   { message }
//   → { statusCode, success, statusMessage, data: { phone, candidate_name } }
//
// ─── WHAT THIS DELIBERATELY DOES NOT SEND ───────────────────────────────────
//
// No token: the server reads the store's own from `store_settings`, so no
// gateway credential is shipped to the browser, and a platform user working
// inside a customer store sends from THAT store's WhatsApp account — the
// browser only ever had one token to offer and got that wrong.
//
// No phone number: the server reads it off the candidate record. A route that
// messaged whatever number the client supplied, under our sender name, would be
// an open relay.
//
// So the only thing that travels is the words — which is the one part a
// recruiter is allowed to change.

/**
 * Put a number into the shape the gateway accepts, for display and for the
 * dialog's "can this person be reached" check.
 *
 * The server normalises independently and its answer is the one that counts —
 * this exists so the dialog can say "no usable number for Ravi" BEFORE anything
 * is sent, rather than showing it as a failure afterwards. Deliberately
 * stricter than `waNumber` in CandidatePipeline, which only has to produce
 * something wa.me will open with a human watching.
 *
 * @returns {string|null} "91XXXXXXXXXX", or null if it cannot be trusted
 */
export function enotifyPhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return null;
}

export const whatsappService = {
  /**
   * Send the invitation to one candidate.
   *
   * Resolves `{ ok: true }` or `{ ok: false, reason }` rather than throwing.
   * Callers send to a list of people and have to report which of them failed
   * and why, so a rejected promise per candidate would only be turned back into
   * this shape by every one of them.
   */
  async send({ applicationId, message }) {
    if (!message?.trim()) return { ok: false, reason: "Nothing to send" };

    try {
      const res = await apiClient.post(`/interview/whatsapp/${applicationId}`, {
        message,
      });
      const body = res?.data;
      if (!body?.success) {
        return { ok: false, reason: body?.statusMessage || "Send failed" };
      }
      return { ok: true, phone: body?.data?.phone || null };
    } catch (err) {
      return {
        ok: false,
        reason: err?.response?.data?.statusMessage || err.message || "Send failed",
      };
    }
  },
};

export default whatsappService;
